import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Grid, Icon, Label, Segment, Table } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showInfo, showSuccess, timestamp2string } from '../../helpers';
import './TopUp.css';

const orderStatusMap = {
  pending: { text: '待支付', color: 'orange' },
  paying: { text: '支付中', color: 'blue' },
  paid: { text: '已入账', color: 'green' },
  failed: { text: '支付失败', color: 'red' },
  canceled: { text: '已取消', color: 'grey' },
  refunded: { text: '已退款', color: 'violet' },
};

const resourcePackageStatusMap = {
  active: { text: '可用', color: 'green' },
  exhausted: { text: '已用尽', color: 'orange' },
  expired: { text: '已过期', color: 'grey' },
  refunded: { text: '已退款', color: 'violet' },
};

const resourcePackageUsageTypeMap = {
  consume: { text: '消耗', color: 'red' },
  refund: { text: '退回', color: 'green' },
};

const transactionTypeMap = {
  topup_order: '订单入账',
  redemption: '兑换码',
  admin_adjust: '人工调整',
  refund: '退款扣回',
};

function formatAmount(value) {
  return `¥${Number(value || 0).toFixed(2)}`;
}

function formatBalanceAmount(quota, quotaPerUnit) {
  const unit = Number(localStorage.getItem('quota_per_unit') || quotaPerUnit || 1000000 / 92);
  return formatAmount(unit > 0 ? Number(quota || 0) / unit : 0);
}

function formatTokenQuota(value) {
  const quota = Number(value || 0);
  if (quota >= 100000000) return `${(quota / 100000000).toFixed(2)} 亿`;
  if (quota >= 10000) return `${(quota / 10000).toFixed(2)} 万`;
  return String(quota);
}

function formatPeriod(period) {
  if (period === '30d') return '30天';
  if (period === 'month') return '1个月';
  if (period === 'year') return '1年';
  return period || '-';
}

function getResourcePackageStatus(subscription) {
  if (
    subscription?.status === 'active' &&
    subscription?.expired_time > 0 &&
    subscription.expired_time * 1000 <= Date.now()
  ) {
    return resourcePackageStatusMap.expired;
  }
  return resourcePackageStatusMap[subscription?.status] || {
    text: subscription?.status || '-',
    color: 'grey',
  };
}

function formatUsageQuotaDelta(record) {
  const delta = Math.abs(Number(record?.quota_delta || 0));
  const sign = record?.event_type === 'refund' ? '+' : '-';
  return `${sign}${formatTokenQuota(delta)} Tokens`;
}

function parseQuickAmounts(value) {
  return String(value || '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => item > 0);
}

const TopUp = () => {
  const { t } = useTranslation();
  const [redemptionCode, setRedemptionCode] = useState('');
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [usageRecords, setUsageRecords] = useState([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(0);
  const [amount, setAmount] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);

  const quickAmounts = useMemo(
    () => parseQuickAmounts(paymentConfig?.quick_amounts),
    [paymentConfig]
  );

  const selectedAmount = Number(amount || quickAmounts[0] || 0);
  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.id === selectedSubscriptionId
      ),
    [subscriptions, selectedSubscriptionId]
  );

  const loadUserQuota = async () => {
    const res = await API.get('/api/user/self');
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
    } else {
      showError(message);
    }
  };

  const loadPaymentConfig = async () => {
    const res = await API.get('/api/topup/config');
    const { success, message, data } = res.data;
    if (success) {
      setPaymentConfig(data);
      const firstAmount = parseQuickAmounts(data.quick_amounts)[0];
      if (firstAmount) setAmount(String(firstAmount));
    } else {
      showError(message);
    }
  };

  const loadPlans = async () => {
    const res = await API.get('/api/topup/plans');
    const { success, message, data } = res.data;
    if (success) {
      setPlans(data || []);
    } else {
      showError(message);
    }
  };

  const loadOrders = async () => {
    const res = await API.get('/api/topup/orders?p=0');
    const { success, message, data } = res.data;
    if (success) {
      setOrders(data || []);
    } else {
      showError(message);
    }
  };

  const loadTransactions = async () => {
    const res = await API.get('/api/billing/transactions?p=0');
    const { success, message, data } = res.data;
    if (success) {
      setTransactions(data || []);
    } else {
      showError(message);
    }
  };

  const loadResourcePackageSubscriptions = async () => {
    const res = await API.get('/api/topup/subscriptions');
    const { success, message, data } = res.data;
    if (success) {
      const list = data || [];
      setSubscriptions(list);
      setSelectedSubscriptionId((currentId) => {
        if (currentId && list.some((item) => item.id === currentId)) {
          return currentId;
        }
        return list[0]?.id || 0;
      });
      return list;
    }
    showError(message);
    return [];
  };

  const loadResourcePackageUsage = async (subscriptionId = selectedSubscriptionId) => {
    const id = Number(subscriptionId || 0);
    if (!id) {
      setUsageRecords([]);
      return;
    }
    setIsLoadingUsage(true);
    try {
      const res = await API.get(
        `/api/topup/subscription-usage?p=0&subscription_id=${id}`
      );
      const { success, message, data } = res.data;
      if (success) {
        setUsageRecords(data || []);
      } else {
        showError(message);
      }
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const refreshResourcePackages = async () => {
    const list = await loadResourcePackageSubscriptions();
    const nextId = selectedSubscriptionId && list.some((item) => item.id === selectedSubscriptionId)
      ? selectedSubscriptionId
      : list[0]?.id || 0;
    if (nextId) {
      await loadResourcePackageUsage(nextId);
    } else {
      setUsageRecords([]);
    }
  };

  useEffect(() => {
    loadPaymentConfig().catch(showError);
    loadPlans().catch(showError);
    loadUserQuota().catch(showError);
    loadOrders().catch(showError);
    loadTransactions().catch(showError);
    loadResourcePackageSubscriptions().catch(showError);
  }, []);

  useEffect(() => {
    if (selectedSubscriptionId) {
      loadResourcePackageUsage(selectedSubscriptionId).catch(showError);
    } else {
      setUsageRecords([]);
    }
  }, [selectedSubscriptionId]);

  const createPaymentOrder = async (plan) => {
    if (!paymentConfig?.enabled) {
      showError('在线充值暂未开启，请联系管理员或使用兑换码充值');
      return;
    }
    if (!plan && (!selectedAmount || selectedAmount <= 0)) {
      showError('请输入有效充值金额');
      return;
    }
    setIsCreatingOrder(true);
    try {
      const payload = plan
        ? { plan_id: plan.id }
        : {
            amount: selectedAmount,
          };
      const res = await API.post('/api/topup/orders', payload);
      const { success, message, data } = res.data;
      if (success) {
        showSuccess('充值订单已创建');
        await loadOrders();
        await loadResourcePackageSubscriptions();
        if (data?.redirect_url) {
          window.open(data.redirect_url, '_blank', 'noopener,noreferrer');
        } else {
          showInfo('订单已创建，请等待管理员确认入账');
        }
      } else {
        showError(message);
      }
    } catch (err) {
      showError(err);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const redeemCode = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsRedeeming(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => quota + data);
        setRedemptionCode('');
        await loadTransactions();
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <div className='dashboard-container'>
      <div className='topup-shell'>
        <section className='topup-hero'>
          <div>
            <Label className='topup-eyebrow'>账户充值</Label>
            <h2>充值账户余额并查看入账进度</h2>
            <p>可通过在线支付创建充值订单，也可以使用管理员发放的兑换码。</p>
          </div>
          <div className='topup-balance'>
            <span>当前余额</span>
            <strong>{formatBalanceAmount(userQuota, paymentConfig?.quota_per_unit)}</strong>
          </div>
        </section>

        {plans.length > 0 && (
          <Segment className='topup-panel topup-plan-panel'>
            <div className='topup-panel-head'>
              <div>
                <h3>资源包订阅</h3>
                <p>AICC-Doubao-Seedance-2.0 资源包，30 天有效，抵扣在线推理 Tokens 消耗。</p>
              </div>
              <Label color='blue'>{plans.length} 个资源包</Label>
            </div>
            <div className='topup-plan-grid'>
              {plans.map((plan) => (
                <div className='topup-plan' key={plan.id}>
                  <div>
                    <Label color={plan.billing_mode === 'subscription' ? 'teal' : plan.billing_mode === 'yearly' ? 'violet' : plan.billing_mode === 'monthly' ? 'blue' : 'green'}>
                      {plan.billing_mode === 'subscription'
                        ? '订阅'
                        : plan.billing_mode === 'yearly'
                          ? '年度'
                          : plan.billing_mode === 'monthly'
                            ? '月度'
                            : '按量'}
                    </Label>
                    <Label basic color='blue'>
                      {formatPeriod(plan.period)}
                    </Label>
                    <h3>{plan.name}</h3>
                    <p>{plan.description || 'AICC-Doubao-Seedance-2.0 资源包，30 天有效，可抵扣模型在线推理 Tokens 消耗。'}</p>
                  </div>
                  <div className='topup-plan-price'>
                    <strong>{formatAmount(plan.price)}</strong>
                    <span>资源包 {formatTokenQuota(plan.quota)} Tokens</span>
                    <span>目录价 {Number(plan.unit_price || 0).toFixed(6)} 元/Token</span>
                  </div>
                  <Button
                    primary
                    fluid
                    onClick={() => createPaymentOrder(plan)}
                    loading={isCreatingOrder}
                    disabled={isCreatingOrder || !paymentConfig?.enabled}
                  >
                    购买资源包
                  </Button>
                </div>
              ))}
            </div>
          </Segment>
        )}

        <Card fluid className='topup-order-card topup-resource-card'>
          <Card.Content>
            <div className='topup-panel-head'>
              <div>
                <h3>我的资源包</h3>
                <p>查看已订阅资源包的剩余额度、有效期和每次扣量明细。</p>
              </div>
              <Button basic icon labelPosition='left' onClick={refreshResourcePackages}>
                <Icon name='refresh' />
                刷新
              </Button>
            </div>
            <Table basic='very' className='topup-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>资源包</Table.HeaderCell>
                  <Table.HeaderCell>总量</Table.HeaderCell>
                  <Table.HeaderCell>已用 / 剩余</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>有效期</Table.HeaderCell>
                  <Table.HeaderCell>明细</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subscriptions.map((subscription) => {
                  const status = getResourcePackageStatus(subscription);
                  return (
                    <Table.Row key={subscription.id}>
                      <Table.Cell>
                        <strong>{subscription.plan_name}</strong>
                        <span className='topup-muted'>{subscription.model_name}</span>
                      </Table.Cell>
                      <Table.Cell>{formatTokenQuota(subscription.quota)} Tokens</Table.Cell>
                      <Table.Cell>
                        {formatTokenQuota(subscription.used_quota)} /{' '}
                        {formatTokenQuota(subscription.remaining_quota)} Tokens
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={status.color}>{status.text}</Label>
                      </Table.Cell>
                      <Table.Cell>
                        {timestamp2string(subscription.started_time)}
                        <span className='topup-muted'>
                          至 {timestamp2string(subscription.expired_time)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          size='mini'
                          primary={selectedSubscriptionId === subscription.id}
                          basic={selectedSubscriptionId !== subscription.id}
                          onClick={() => setSelectedSubscriptionId(subscription.id)}
                        >
                          查看明细
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {subscriptions.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='6' textAlign='center'>
                      暂无资源包订阅
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {selectedSubscription && (
              <div className='topup-usage-detail'>
                <div className='topup-panel-head'>
                  <div>
                    <h3>{selectedSubscription.plan_name} 用量明细</h3>
                    <p>
                      剩余 {formatTokenQuota(selectedSubscription.remaining_quota)} Tokens，到期{' '}
                      {timestamp2string(selectedSubscription.expired_time)}
                    </p>
                  </div>
                  <Button
                    basic
                    icon
                    labelPosition='left'
                    loading={isLoadingUsage}
                    onClick={() => loadResourcePackageUsage(selectedSubscriptionId)}
                  >
                    <Icon name='refresh' />
                    刷新明细
                  </Button>
                </div>
                <Table basic='very' className='topup-table'>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>时间</Table.HeaderCell>
                      <Table.HeaderCell>类型</Table.HeaderCell>
                      <Table.HeaderCell>令牌</Table.HeaderCell>
                      <Table.HeaderCell>Tokens 变动</Table.HeaderCell>
                      <Table.HeaderCell>剩余</Table.HeaderCell>
                      <Table.HeaderCell>金额</Table.HeaderCell>
                      <Table.HeaderCell>来源 / 备注</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {usageRecords.map((record) => {
                      const usageType = resourcePackageUsageTypeMap[record.event_type] || {
                        text: record.event_type || '-',
                        color: 'grey',
                      };
                      const amount = Number(record.amount_rmb || 0);
                      return (
                        <Table.Row key={record.id}>
                          <Table.Cell>{timestamp2string(record.created_time)}</Table.Cell>
                          <Table.Cell>
                            <Label color={usageType.color}>{usageType.text}</Label>
                          </Table.Cell>
                          <Table.Cell>
                            {record.token_name || '-'}
                            {record.token_id ? (
                              <span className='topup-muted'>#{record.token_id}</span>
                            ) : null}
                          </Table.Cell>
                          <Table.Cell>
                            <Label color={usageType.color}>
                              {formatUsageQuotaDelta(record)}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>
                            {formatTokenQuota(record.remaining_after)} Tokens
                          </Table.Cell>
                          <Table.Cell>
                            {amount === 0
                              ? '-'
                              : `${amount > 0 ? '+' : '-'}${formatAmount(Math.abs(amount))}`}
                          </Table.Cell>
                          <Table.Cell>
                            {record.source_no || record.source_type || '-'}
                            {record.remark ? (
                              <span className='topup-muted'>{record.remark}</span>
                            ) : null}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                    {usageRecords.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan='7' textAlign='center'>
                          暂无资源包用量明细
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            )}
          </Card.Content>
        </Card>

        <Grid stackable columns={2}>
          <Grid.Column width={10}>
            <Segment className='topup-panel'>
              <div className='topup-panel-head'>
                <div>
                  <h3>在线充值</h3>
                  <p>{paymentConfig?.enabled ? paymentConfig.name : '后台暂未开启在线支付'}</p>
                </div>
                <Label color={paymentConfig?.enabled ? 'green' : 'grey'}>
                  {paymentConfig?.enabled ? '可用' : '未启用'}
                </Label>
              </div>

              <div className='topup-amount-grid'>
                {quickAmounts.map((item) => (
                  <button
                    type='button'
                    key={item}
                    className={Number(amount) === item ? 'active' : ''}
                    onClick={() => setAmount(String(item))}
                  >
                    {formatAmount(item)}
                  </button>
                ))}
              </div>

              <Form className='topup-form'>
                <Form.Input
                  label='自定义金额'
                  type='number'
                  min={paymentConfig?.min_amount || 1}
                  max={paymentConfig?.max_amount || 99999}
                  value={amount}
                  onChange={(_, { value }) => setAmount(value)}
                  action={{
                    content: '创建订单',
                    primary: true,
                    loading: isCreatingOrder,
                    disabled: isCreatingOrder,
                    onClick: () => createPaymentOrder(),
                  }}
                />
              </Form>

              <div className='topup-estimate'>
                <span>预计入账</span>
                <strong>{formatAmount(selectedAmount)}</strong>
                <small>
                  金额范围 {formatAmount(paymentConfig?.min_amount || 1)} -{' '}
                  {formatAmount(paymentConfig?.max_amount || 99999)}
                </small>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column width={6}>
            <Segment className='topup-panel topup-code-panel'>
              <div className='topup-panel-head'>
                <div>
                  <h3>兑换码充值</h3>
                  <p>输入兑换码后余额会立即入账。</p>
                </div>
                <Icon name='ticket alternate' />
              </div>
              <Form.Input
                fluid
                icon='key'
                iconPosition='left'
                placeholder={t('topup.redeem_code.placeholder')}
                value={redemptionCode}
                onChange={(e) => setRedemptionCode(e.target.value)}
                onPaste={(e) => {
                  e.preventDefault();
                  setRedemptionCode(e.clipboardData.getData('text').trim());
                }}
              />
              <Button
                color='green'
                fluid
                onClick={redeemCode}
                loading={isRedeeming}
                disabled={isRedeeming}
              >
                {isRedeeming ? t('topup.redeem_code.submitting') : t('topup.redeem_code.submit')}
              </Button>
            </Segment>
          </Grid.Column>
        </Grid>

        <Card fluid className='topup-order-card'>
          <Card.Content>
            <div className='topup-panel-head'>
              <div>
                <h3>充值订单</h3>
                <p>支付完成后，等待管理员确认即可入账。</p>
              </div>
              <Button basic icon labelPosition='left' onClick={loadOrders}>
                <Icon name='refresh' />
                刷新
              </Button>
            </div>
            <Table basic='very' className='topup-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>订单号</Table.HeaderCell>
                  <Table.HeaderCell>金额</Table.HeaderCell>
                  <Table.HeaderCell>入账 / 资源包</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>创建时间</Table.HeaderCell>
                  <Table.HeaderCell>支付</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {orders.map((order) => {
                  const status = orderStatusMap[order.status] || orderStatusMap.pending;
                  return (
                    <Table.Row key={order.id}>
                      <Table.Cell>
                        <strong>{order.order_no}</strong>
                        {order.trade_no && <span className='topup-muted'>交易号 {order.trade_no}</span>}
                      </Table.Cell>
                      <Table.Cell>{formatAmount(order.amount)}</Table.Cell>
                      <Table.Cell>
                        {order.plan_id > 0
                          ? `${formatTokenQuota(order.quota)} Tokens`
                          : formatBalanceAmount(order.quota, paymentConfig?.quota_per_unit)}
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={status.color}>{status.text}</Label>
                      </Table.Cell>
                      <Table.Cell>{timestamp2string(order.created_time)}</Table.Cell>
                      <Table.Cell>
                        {order.status === 'pending' && order.payment_url ? (
                          <Button
                            size='mini'
                            primary
                            onClick={() => window.open(order.payment_url, '_blank', 'noopener,noreferrer')}
                          >
                            去支付
                          </Button>
                        ) : (
                          <span className='topup-muted'>-</span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {orders.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='6' textAlign='center'>
                      暂无充值订单
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card.Content>
        </Card>

        <Card fluid className='topup-order-card'>
          <Card.Content>
            <div className='topup-panel-head'>
              <div>
                <h3>余额流水</h3>
                <p>每一笔入账、退款和人工调整都会记录变动前后余额。</p>
              </div>
              <Button basic icon labelPosition='left' onClick={loadTransactions}>
                <Icon name='refresh' />
                刷新
              </Button>
            </div>
            <Table basic='very' className='topup-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>来源</Table.HeaderCell>
                  <Table.HeaderCell>变动</Table.HeaderCell>
                  <Table.HeaderCell>变动后余额</Table.HeaderCell>
                  <Table.HeaderCell>备注</Table.HeaderCell>
                  <Table.HeaderCell>时间</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {transactions.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <strong>{transactionTypeMap[item.type] || item.type}</strong>
                      <span className='topup-muted'>{item.source_no || item.source_type || '-'}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={Number(item.amount_rmb || 0) >= 0 ? 'green' : 'red'}>
                        {Number(item.amount_rmb || 0) >= 0 ? '+' : '-'}
                        {formatAmount(Math.abs(Number(item.amount_rmb || 0)))}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{formatAmount(item.balance_after_rmb)}</Table.Cell>
                    <Table.Cell>{item.remark || '-'}</Table.Cell>
                    <Table.Cell>{timestamp2string(item.created_time)}</Table.Cell>
                  </Table.Row>
                ))}
                {transactions.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>
                      暂无余额流水
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default TopUp;
